import React from 'react';
import { useTranslation } from 'react-i18next';
import AttendeeForm from './attendee-form/attendeeForm';
import Form from '../../misc/form/form';
import Button from '../../ui/button/button';
import { generate_use_form_schema } from '../../../../steps/formStep';
import useForm from '../../../../utils/useForm';
import { ErrorMessage } from '../../misc/message/message';
import './formStep.css';
import { iframeScrollTo } from '../../../../utils/scroll';
import CheckboxCard from '../../cards/checkboxCard/checkboxCard';

export default function FormStep({config = {}, onSubmit = () => {}, isSubmitting = false, isGoingBack = false, step = {}}) {
    const { t } = useTranslation();
    const { form_attendees } = config.form_step.attendees;
    const { form_buyer } = config.form_step.buyer;
    const { errors: formErrorsBuyerBackend } = form_buyer;
    const cgv = config.form_step.buyer.cgv;

    const [checkedCGV, setCheckedCGV] = React.useState(false);

    const [displayErrorsFrontendMessage, setDisplayErrorsFrontendMessage] = React.useState(false);
    const [showMore, setShowMore] = React.useState(
        Object.fromEntries(
            Object.keys(
                form_attendees
            ).map(
                (attendee_key, index) => {
                    if (index === 0) {
                        return [attendee_key, true];
                    }
                    return [attendee_key, false];
                }
            )
        )
    );

    const initialAttendeeForm = (fields) => {
        return fields.reduce((acc, field) => {
            let {key, value, default_value, type} = field;
            return {...acc, [key]: type === 'checkbox' ? default_value : value};
        }, {});
    };

    const onChangeCgv = React.useCallback((e, {checked}) => {
        setCheckedCGV(checked);
    }, []);

    const [attendees, setAttendees] = React.useState(
        Object.fromEntries(
            Object.entries(
                form_attendees
            ).map(
                ([attendee_key, attendee], index) => {
                    return [attendee_key, {...attendee.header, form: initialAttendeeForm(attendee.fields)}];
                }
            )
        )
    );

    let buyerSchema = React.useRef(generate_use_form_schema(form_buyer.fields));

    const [form, setFormValue, setFormObject, , formBuyerErrors, onValidateBuyer, onChangeErrors] = useForm(buyerSchema.current, {
        validateOnChange: false,
        clearOnChange: true,
        onChange: () => {}
    });

    const isBuyerInvalid = React.useMemo(() => {
        return !checkedCGV ||Object.keys(formBuyerErrors).length > 0;
    }, [formBuyerErrors, checkedCGV]);

    const isDisabledSubmit = React.useMemo(() => {
        return isBuyerInvalid || isGoingBack;
    }, [isBuyerInvalid, isGoingBack]);

    // if validation errors back - scroll to the top and open attendee cards that are missing informations
    React.useEffect(() => {
        if (step.data.error_messages.length || isBuyerInvalid) {
            iframeScrollTo(document.querySelector(`.wz-neo-layout`));
            setShowMore(previousState => {
                let newState = {...previousState};
                Object.entries(
                    form_attendees
                ).forEach(([key, value]) => {
                    if (Object.keys(value.errors).length > 0) {
                        newState[key] = true;
                    }
                })
                return newState;
            })
        }
    }, [step, isBuyerInvalid]);

    const _onSubmit = React.useCallback(() => {
        if (isDisabledSubmit) {
            return;
        }
        let { valid } = onValidateBuyer()
        let validAttendees = true;
        let updateShowMoreAttendees = {}
        //Attendees form validation
        Object.entries(attendees).forEach(([key, {onValidate}]) => {
            let res = onValidate()
            validAttendees = validAttendees && res.valid;
            updateShowMoreAttendees[key] = !res.valid;
        })

        if (!valid || !validAttendees) {
            //open only attendeeCard(s) with errors
            setShowMore(updateShowMoreAttendees);
            setDisplayErrorsFrontendMessage(true);
            iframeScrollTo(document.querySelector(`.wz-neo-layout`));
            return;
        }
        setDisplayErrorsFrontendMessage(false);
        onSubmit({
            data: {
                buyer: form,
                attendees: Object.fromEntries(Object.entries(attendees).map(([key, value]) => [key, value.form]))
            }
        });
    },[form, attendees, isDisabledSubmit, onValidateBuyer]);

    React.useEffect(() => {
        if (Object.keys(formErrorsBuyerBackend).length) {
            onChangeErrors({...formErrorsBuyerBackend});
        }
    },[formErrorsBuyerBackend]);

    const eventCountry = React.useMemo(() => step?.data?.event_header?.address?._country, [step]);

    return (
        <>
            {displayErrorsFrontendMessage && <ErrorMessage config={{
                error_messages: {
                    title: t('widget.front_error_message.title'),
                    body: t('widget.front_error_message.description')
                }
            }}/>}
            <div className='neo-form-step'>
                <h1 className='neo-form-step-title'>{config.form_step.buyer.header.title_section}</h1>
                <Form form={form} setFormValue={setFormValue} setFormObject={setFormObject} fields={form_buyer.fields} formIndex='buyer' formErrors={formBuyerErrors} country={eventCountry}/>
                <h1 className='neo-form-step-title'>{config.form_step.attendees.header.title_section}</h1>
                <h2 className='neo-form-step-quantity'>{config.form_step.attendees.header.ticket_count_section}</h2>
                {Object.entries(form_attendees).map(([key, attendee], index) => <AttendeeForm key={key} index={key} attendee={attendee} setAttendees={setAttendees} showMore={showMore} setShowMore={setShowMore} eventCountry={eventCountry} formBuyer={form}/>)}
                <CheckboxCard title={cgv.title} description={cgv.description} links={cgv.links} onChange={onChangeCgv}/>
                <div className='neo-cta-container'>
                    <Button type='primary' className='neo-cta-next' isLoading={isSubmitting} onClick={_onSubmit} label={t('widget.resale.tickets.cta-next')} disabled={isDisabledSubmit}/>
                </div>
            </div>
        </>
    )
}